import { AgeCategoryInTournament, Gender, WeightCategoryInTournament } from './apiClient';
import { filterAgeByGender, filterWeightByGender } from './genderCategoryUtils';

export interface WeightCategoryDraft {
  sortOrder: number;
  maxWeight?: number;
  isOpenEnded: boolean;
  gender?: Gender;
}

export interface AgeCategoryDraft {
  sortOrder: number;
  maxAge?: number;
  isOpenEnded: boolean;
  name?: string;
  gender?: Gender;
}

const sortWeight = (categories: WeightCategoryInTournament[], gender?: Gender | null) =>
  filterWeightByGender(categories, gender).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const sortAge = (categories: AgeCategoryInTournament[], gender?: Gender | null) =>
  filterAgeByGender(categories, gender).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const formatNumber = (value: number) =>
  Math.abs(value % 1) < 0.001 ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, '');

export const resolveWeightCategory = (
  weight: number,
  categories: WeightCategoryInTournament[],
  personGender?: Gender | null
): WeightCategoryInTournament | undefined =>
  resolveByLimit(weight, sortWeight(categories, personGender), (c) => c.isOpenEnded ?? false, (c) => c.maxWeight);

export const resolveAgeCategory = (
  age: number,
  categories: AgeCategoryInTournament[],
  personGender?: Gender | null
): AgeCategoryInTournament | undefined =>
  resolveByLimit(age, sortAge(categories, personGender), (c) => c.isOpenEnded ?? false, (c) => c.maxAge);

function resolveByLimit<T extends { sortOrder?: number }>(
  value: number,
  sorted: T[],
  isOpenEnded: (c: T) => boolean,
  getMax: (c: T) => number | undefined | null
): T | undefined {
  for (let i = 0; i < sorted.length; i++) {
    const cat = sorted[i];
    const prevMax = i > 0 ? getMax(sorted[i - 1]) : null;

    if (isOpenEnded(cat)) {
      if (prevMax == null || value > prevMax) return cat;
    } else {
      const max = getMax(cat);
      if (max != null && value <= max && (prevMax == null || value > prevMax)) return cat;
    }
  }
  return undefined;
}

export const formatWeightCategoryLabel = (
  category: WeightCategoryInTournament,
  allCategories: WeightCategoryInTournament[]
): string => {
  const sorted = sortWeight(allCategories, category.gender);
  const index = findWeightIndex(sorted, category);

  if (category.isOpenEnded) {
    const prevMax = index > 0 ? sorted[index - 1].maxWeight : undefined;
    return prevMax != null ? `${formatNumber(prevMax)}+ кг` : 'Тяжёлый вес';
  }

  return category.maxWeight != null ? `До ${formatNumber(category.maxWeight)} кг` : '—';
};

/** Значение для строки «весовая категория до __ кг» в печатной форме. */
export const formatWeightCategoryKgForPrint = (
  category: WeightCategoryInTournament,
  allCategories: WeightCategoryInTournament[]
): string => {
  const sorted = sortWeight(allCategories, category.gender);
  const index = findWeightIndex(sorted, category);

  if (category.isOpenEnded) {
    const prevMax = index > 0 ? sorted[index - 1].maxWeight : undefined;
    return prevMax != null ? `${formatNumber(prevMax)}+` : 'Тяж';
  }

  return category.maxWeight != null ? formatNumber(category.maxWeight) : '';
};

export const formatAgeCategoryLabel = (
  category: AgeCategoryInTournament,
  allCategories: AgeCategoryInTournament[]
): string => {
  if (category.name?.trim()) return category.name.trim();

  const sorted = sortAge(allCategories, category.gender);
  const index = findAgeIndex(sorted, category);

  if (category.isOpenEnded) {
    const prevMax = index > 0 ? sorted[index - 1].maxAge : undefined;
    return prevMax != null ? `${prevMax}+ лет` : 'Без ограничения';
  }

  return category.maxAge != null ? `До ${category.maxAge} лет` : '—';
};

/** Название группы участников для печати (Юноши, Мужчины…). */
export const resolveParticipantCategoryLabel = (
  category: AgeCategoryInTournament,
  allCategories: AgeCategoryInTournament[]
): string => formatAgeCategoryLabel(category, allCategories);

export const formatWeightDraftLabel = (draft: WeightCategoryDraft, all: WeightCategoryDraft[]): string => {
  const sorted = [...all].sort((a, b) => a.sortOrder - b.sortOrder);
  const index = sorted.findIndex((c) => c.sortOrder === draft.sortOrder);
  if (draft.isOpenEnded) {
    const prevMax = index > 0 ? sorted[index - 1].maxWeight : undefined;
    return prevMax != null ? `${formatNumber(prevMax)}+ кг` : 'Тяжёлый вес';
  }
  return draft.maxWeight != null ? `До ${formatNumber(draft.maxWeight)} кг` : '—';
};

export const formatAgeDraftLabel = (draft: AgeCategoryDraft, all: AgeCategoryDraft[]): string => {
  if (draft.name?.trim()) return draft.name.trim();
  const sorted = [...all].sort((a, b) => a.sortOrder - b.sortOrder);
  const index = sorted.findIndex((c) => c.sortOrder === draft.sortOrder);
  if (draft.isOpenEnded) {
    const prevMax = index > 0 ? sorted[index - 1].maxAge : undefined;
    return prevMax != null ? `${prevMax}+ лет` : 'Без ограничения';
  }
  return draft.maxAge != null ? `До ${draft.maxAge} лет` : '—';
};

export const calculateAge = (birthDate: Date, atDate: Date): number => {
  let age = atDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = atDate.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && atDate.getDate() < birthDate.getDate())) age--;
  return age;
};

const validateWeightList = (categories: WeightCategoryDraft[], prefix: string): string | null => {
  if (!categories.length) return null;
  const ordered = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
  let prevMax: number | undefined;
  let openCount = 0;
  for (let i = 0; i < ordered.length; i++) {
    const cat = ordered[i];
    if (cat.isOpenEnded) {
      openCount++;
      if (i !== ordered.length - 1) return `${prefix}Открытая весовая категория может быть только последней`;
      continue;
    }
    if (!cat.maxWeight || cat.maxWeight <= 0) return `${prefix}Укажите положительный лимит веса`;
    if (prevMax != null && cat.maxWeight <= prevMax)
      return `${prefix}Лимиты весовых категорий должны строго возрастать`;
    prevMax = cat.maxWeight;
  }
  if (openCount > 1) return `${prefix}Допускается не более одной открытой весовой категории`;
  return null;
};

const validateAgeList = (categories: AgeCategoryDraft[], prefix: string): string | null => {
  if (!categories.length) return null;
  const ordered = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
  let prevMax: number | undefined;
  let openCount = 0;
  for (let i = 0; i < ordered.length; i++) {
    const cat = ordered[i];
    if (!cat.name?.trim()) {
      return `${prefix}Укажите название для каждой возрастной категории (например: Юноши, Мужчины, Девушки)`;
    }
    if (cat.isOpenEnded) {
      openCount++;
      if (i !== ordered.length - 1) return `${prefix}Открытая возрастная категория может быть только последней`;
      continue;
    }
    if (!cat.maxAge || cat.maxAge <= 0) return `${prefix}Укажите положительный лимит возраста`;
    if (prevMax != null && cat.maxAge <= prevMax)
      return `${prefix}Лимиты возрастных категорий должны строго возрастать`;
    prevMax = cat.maxAge;
  }
  if (openCount > 1) return `${prefix}Допускается не более одной открытой возрастной категории`;
  return null;
};

export const validateWeightDrafts = (categories: WeightCategoryDraft[], groupLabel?: string): string | null => {
  const prefix = groupLabel ? `${groupLabel}: ` : '';
  if (!categories.length) return `${prefix}Укажите хотя бы одну весовую категорию`;
  return validateWeightList(categories, prefix);
};

export const validateAgeDrafts = (categories: AgeCategoryDraft[], groupLabel?: string): string | null => {
  const prefix = groupLabel ? `${groupLabel}: ` : '';
  if (!categories.length) return `${prefix}Укажите хотя бы одну возрастную категорию`;
  return validateAgeList(categories, prefix);
};

export const validateGenderedTournamentDrafts = (
  boysWeight: WeightCategoryDraft[],
  boysAge: AgeCategoryDraft[],
  girlsWeight: WeightCategoryDraft[],
  girlsAge: AgeCategoryDraft[]
): string | null => {
  const hasBoys = boysWeight.length > 0 || boysAge.length > 0;
  const hasGirls = girlsWeight.length > 0 || girlsAge.length > 0;

  if (!hasBoys && !hasGirls) {
    return 'Укажите категории хотя бы для одного пола';
  }

  if (hasBoys) {
    if (!boysWeight.length) return 'Мальчики: укажите весовые категории';
    if (!boysAge.length) return 'Мальчики: укажите возрастные категории';
    const w = validateWeightDrafts(boysWeight, 'Мальчики');
    if (w) return w;
    const a = validateAgeDrafts(boysAge, 'Мальчики');
    if (a) return a;
  }

  if (hasGirls) {
    if (!girlsWeight.length) return 'Девочки: укажите весовые категории';
    if (!girlsAge.length) return 'Девочки: укажите возрастные категории';
    const w = validateWeightDrafts(girlsWeight, 'Девочки');
    if (w) return w;
    const a = validateAgeDrafts(girlsAge, 'Девочки');
    if (a) return a;
  }

  return null;
};

const findWeightIndex = (sorted: WeightCategoryInTournament[], category: WeightCategoryInTournament) => {
  if (category.id) {
    const byId = sorted.findIndex((c) => c.id === category.id);
    if (byId >= 0) return byId;
  }
  return sorted.findIndex((c) => c.sortOrder === category.sortOrder);
};

const findAgeIndex = (sorted: AgeCategoryInTournament[], category: AgeCategoryInTournament) => {
  if (category.id) {
    const byId = sorted.findIndex((c) => c.id === category.id);
    if (byId >= 0) return byId;
  }
  return sorted.findIndex((c) => c.sortOrder === category.sortOrder);
};

export const toWeightDraftsFromTournament = (
  categories: WeightCategoryInTournament[],
  gender?: Gender
): WeightCategoryDraft[] =>
  sortWeight(categories, gender).map((c) => ({
    sortOrder: c.sortOrder ?? 0,
    maxWeight: c.maxWeight,
    isOpenEnded: c.isOpenEnded ?? false,
    gender: c.gender,
  }));

export const toAgeDraftsFromTournament = (
  categories: AgeCategoryInTournament[],
  gender?: Gender
): AgeCategoryDraft[] =>
  sortAge(categories, gender).map((c) => ({
    sortOrder: c.sortOrder ?? 0,
    maxAge: c.maxAge,
    isOpenEnded: c.isOpenEnded ?? false,
    name: c.name,
    gender: c.gender,
  }));

export const splitDraftsByGender = (weights: WeightCategoryDraft[], ages: AgeCategoryDraft[]) => ({
  boysWeight: weights.filter((w) => w.gender === Gender.Male),
  boysAge: ages.filter((a) => a.gender === Gender.Male),
  girlsWeight: weights.filter((w) => w.gender === Gender.Female),
  girlsAge: ages.filter((a) => a.gender === Gender.Female),
  legacyWeight: weights.filter((w) => !w.gender),
  legacyAge: ages.filter((a) => !a.gender),
});
